import React, { useState, useEffect, useRef, useCallback } from "react";
import { SearchBarWrapper, SearchText } from "./Styled";
import SearchIcon from "../../svg/SearchIcon";
import { debounce } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";

interface SearchBarProps {
    placeholder: string;
    fetchItems: Function;
    onFocus?: any;
    style? : React.CSSProperties;
}

const DEBOUNCE_TIME = 300;

const SearchBar = ({ placeholder, fetchItems, onFocus, style }: SearchBarProps) => {
    const [text, setText] = useState("");
    const [isFocus, setIsFocus] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const initialLoad = useRef(true); // Track initial load

    // Debounced fetch function - using useCallback to ensure stability
    const debounceFetchItems = useCallback(
        debounce((searchQuery: any) => {
            fetchItems({ ...searchQuery, page: 1 }); // reset to page 1 as search results change
            
            // Update URL with search parameter while preserving tab parameter
            const params = new URLSearchParams(location.search);
            
            // Preserve the current tab parameter
            const currentTab = params.get('tab');
            
            if (searchQuery.search && searchQuery.search.trim() !== '') {
                params.set('search', searchQuery.search);
            } else {
                params.delete('search');
            }
            params.set('page', '1'); // Reset to page 1
            
            // Ensure tab parameter is preserved
            if (currentTab) {
                params.set('tab', currentTab);
            }
            
            const newURL = `${location.pathname}?${params.toString()}`;
            navigate(newURL, { replace: true });
        }, DEBOUNCE_TIME),
        [location.search, location.pathname, navigate, fetchItems]
    );

    // Function to get search query from URL
    const getSearchQueryFromURL = () => {
        const params = new URLSearchParams(location.search);
        const initialFilters: any = {};
        params.forEach((value, key) => {
            initialFilters[key] = value;
        });

        initialFilters["search"] = initialFilters["search"] || "";
        return initialFilters;
    };

    // Handle changes to the search input
    const onSetText = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchQuery = event.target.value;
        setText(searchQuery);
        debounceFetchItems({ search: searchQuery }); // Call the debounced fetch function
    };

    // Handle input focus
    const onFocusInput = (event: any) => {
        setIsFocus(true);
        if (onFocus) {
            onFocus(event);
        }
    };

    // Handle input blur
    const onBlurInput = () => {
        setIsFocus(false);
    };

    // Effect to get the search query from URL and update the text state
    useEffect(() => {
        const searchQuery = getSearchQueryFromURL();
        if (initialLoad.current) {
            setText(searchQuery["search"]); // Update the search query from the URL
            fetchItems({ ...searchQuery }); // Trigger a fetch based on the URL search term
            initialLoad.current = false; // Mark initial load as false
        }
    }, [location.search]); // Remove debounceFetchItems from dependency

    return (
        <SearchBarWrapper isFocus={isFocus} style={{ ...style || {} }}>
            <SearchIcon />
            <SearchText
                data-testid="search-bar"
                placeholder={placeholder}
                value={text}
                onChange={onSetText}
                onFocus={onFocusInput}
                onBlur={onBlurInput}
                spellCheck={false} // Ensure spaces are allowed
            />
        </SearchBarWrapper>
    );
};

export default SearchBar;
